import React, { useState } from 'react';
import { Album } from '../../types/album';
import { theme } from '../../styles/theme';
import {
  secureDiscogsService,
  DiscogsRelease
} from '../../services/secureDiscogsService';
import { SyncProgress } from '../../services/fileStorage';
import { secureSupabaseService } from '../../services/secureSupabaseService';
import { generateSortKey } from '../../utils/sorting';
import { albumUpdateService } from '../../services/albumUpdateService';
import { DiscogsReleaseDetail } from '../../services/secureDiscogsService';
import { discogsService } from '../../services/discogsService';

// Helper function to convert Discogs release to Album format
const convertDiscogsToAlbum = (release: DiscogsReleaseDetail & { originalYear?: number }, albumId: string) => {
  // Extract primary artist and all artists
  const primaryArtist = release.artists?.[0]?.name || 'Unknown Artist';
  const allArtists = release.artists?.map(artist => artist.name) || [primaryArtist];

  // Combine genres and styles
  const genres = [...(release.genres || []), ...(release.styles || [])];

  // Determine if it's various artists
  const isVariousArtists = primaryArtist.toLowerCase().includes('various') ||
                          allArtists.some(artist => artist.toLowerCase().includes('various'));

  // Use proper format analysis to handle multi-disc sets correctly
  const formatInfo = discogsService.analyzeFormat(release.formats);

  // Convert tracklist
  const tracklist = release.tracklist?.map(track => ({
    position: track.position,
    title: track.title,
    duration: track.duration,
    artists: track.artists?.map(artist => artist.name)
  }));

  // Use originalYear from master release if available, otherwise use release year
  const parseYear = (year: any): number => {
    if (!year) return 0;
    return typeof year === 'string' ? parseInt(year, 10) || 0 : year;
  };

  return {
    artist: primaryArtist,
    artists: allArtists,
    title: release.title,
    year: parseYear(release.originalYear) || parseYear(release.year),
    releaseYear: parseYear(release.year),
    genres,
    isVariousArtists,
    discogsId: release.id.toString(),
    masterId: release.master_id?.toString(),
    format: formatInfo,
    tracklist,
    syncMetadata: {
      lastSyncTime: new Date().toISOString(),
      customFields: {},
      syncSources: {
        discogs: true
      }
    }
  };
};

interface CollectionSyncModalProps {
  onClose: () => void;
  onAlbumsAdded: (albums: Album[]) => void;
  existingAlbums: Album[];
}

interface SyncStats {
  totalAlbums: number;
  processed: number;
  succeeded: number;
  failed: number;
  errors: string[];
}

const CollectionSyncModal: React.FC<CollectionSyncModalProps> = ({ onClose, onAlbumsAdded, existingAlbums }) => {
  const [syncing, setSyncing] = useState(false);
  const [syncMode, setSyncMode] = useState<'full' | 'incremental'>('incremental');
  const [forceSync, setForceSync] = useState(false);
  const [collectionProgress, setCollectionProgress] = useState<{
    message: string;
    percentage: number;
    currentItem?: number;
    totalItems?: number;
    currentAlbum?: string;
    isIncremental?: boolean;
    newAlbums?: number;
    skippedAlbums?: number;
  } | null>(null);
  const [processingProgress, setProcessingProgress] = useState<SyncStats | null>(null);
  const [collectionInfo, setCollectionInfo] = useState<{ totalItems: number; totalPages: number } | null>(null);
  const [pendingSyncProgress, setPendingSyncProgress] = useState<SyncProgress | null>(null);
  const [settingUpStorage, setSettingUpStorage] = useState(false);

  const overlayStyle: React.CSSProperties = {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.8)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: theme.zIndex.modal,
  };

  const modalStyle: React.CSSProperties = {
    backgroundColor: theme.colors.surface,
    borderRadius: theme.borderRadius.lg,
    padding: theme.spacing.xl,
    maxWidth: '90vw',
    maxHeight: '90vh',
    overflow: 'auto',
    border: `1px solid ${theme.colors.border}`,
    width: '600px',
  };

  const headerStyle: React.CSSProperties = {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: theme.spacing.lg,
  };

  const titleStyle: React.CSSProperties = {
    fontSize: '1.5rem',
    fontWeight: '700',
    color: theme.colors.text,
  };

  const closeButtonStyle: React.CSSProperties = {
    background: 'none',
    border: 'none',
    fontSize: '1.5rem',
    color: theme.colors.text,
    cursor: 'pointer',
    padding: theme.spacing.sm,
  };

  const buttonStyle: React.CSSProperties = {
    backgroundColor: theme.colors.primary,
    color: 'white',
    border: 'none',
    padding: `${theme.spacing.md} ${theme.spacing.xl}`,
    borderRadius: theme.borderRadius.md,
    fontSize: '1rem',
    cursor: 'pointer',
    width: '100%',
  };

  const progressBarStyle: React.CSSProperties = {
    width: '100%',
    height: '8px',
    backgroundColor: theme.colors.surface,
    borderRadius: theme.borderRadius.sm,
    overflow: 'hidden',
    marginBottom: theme.spacing.md,
  };

  const loadCollectionInfo = async () => {
    try {
      // Get basic collection info - we'll implement this with the secure service if needed
      setCollectionInfo({ totalItems: 0, totalPages: 0 });
    } catch (error: any) {
      console.error('Failed to load collection info:', error);
      alert(`Failed to load collection info: ${error.message}`);
    }
  };

  React.useEffect(() => {
    const initializeModal = async () => {
      // Load collection info
      if (!collectionInfo) {
        await loadCollectionInfo();
      }

      // Check for pending sync progress
      try {
        const settings = await secureSupabaseService.getSettings();
        const progress = settings.sync_progress;
        if (progress && !progress.isComplete) {
          // Check if the sync session is recent (less than 24 hours old)
          const lastUpdate = new Date(progress.lastUpdateTime);
          const now = new Date();
          const hoursSinceUpdate = (now.getTime() - lastUpdate.getTime()) / (1000 * 60 * 60);
          
          if (hoursSinceUpdate < 24) {
            setPendingSyncProgress(progress);
          } else {
            // Clear old sync progress
            await secureSupabaseService.updateSettings({ sync_progress: undefined });
          }
        }
      } catch (error) {
        console.warn('Failed to load sync progress:', error);
      }
    };

    initializeModal();
  }, []);

  const handleResumeSync = async () => {
    if (!pendingSyncProgress) return;
    
    setSyncing(true);
    setSyncMode(pendingSyncProgress.mode);
    setPendingSyncProgress(null);
    
    // Continue from where we left off
    await handleSyncCollection(pendingSyncProgress);
  };

  const handleClearProgress = async () => {
    await secureSupabaseService.updateSettings({ sync_progress: undefined });
    setPendingSyncProgress(null);
  };

  const handleSetupStorage = async () => {
    setSettingUpStorage(true);
    try {
      await secureSupabaseService.initialize();
      alert('✅ Storage setup complete! Your data is now saved to IndexedDB.');
    } catch (error: any) {
      console.error('Storage setup failed:', error);
      alert(`❌ Storage setup failed: ${error.message}`);
    } finally {
      setSettingUpStorage(false);
    }
  };

  const handleSyncCollection = async (resumeProgress?: SyncProgress) => {
    setSyncing(true);
    setCollectionProgress(null);
    setProcessingProgress(null);

    // Ensure storage is initialized before starting sync
    try {
      await secureSupabaseService.initialize();
    } catch (error: any) {
      console.error('Storage initialization failed:', error);
      alert(`Storage initialization failed: ${error.message}`);
      setSyncing(false);
      return;
    }

    // Create or resume sync session
    const sessionId = resumeProgress?.sessionId || `sync-${Date.now()}`;
    let processedInstanceIds = resumeProgress?.processedInstanceIds || [];
    
    let currentProgress: SyncProgress;
    if (!resumeProgress) {
      // Start new sync session
      currentProgress = {
        sessionId,
        mode: syncMode,
        totalItems: collectionInfo?.totalItems || 0,
        processedItems: 0,
        successfulItems: 0,
        failedItems: 0,
        startTime: new Date().toISOString(),
        lastUpdateTime: new Date().toISOString(),
        processedInstanceIds: [],
        errors: [],
        isComplete: false
      };
      
      await secureSupabaseService.updateSettings({ sync_progress: currentProgress });
    } else {
      currentProgress = resumeProgress;
    }

    try {
      // Step 1: Fetch collection from Discogs using secure API
      let collectionItems: DiscogsRelease[] = [];
      const isIncremental = syncMode === 'incremental' && existingAlbums.length > 0;
      
      setCollectionProgress({ 
        message: 'Fetching collection from Discogs...', 
        percentage: 0,
        isIncremental 
      });
      
      try {
        const allReleases = await secureDiscogsService.getAllReleases();
        
        setCollectionProgress({ 
          message: `Processing ${allReleases.length} albums from Discogs...`, 
          percentage: 50,
          totalItems: allReleases.length,
          isIncremental 
        });
        
        if (isIncremental) {
          // Filter to only new albums not in existing collection
          const existingDiscogsIds = new Set(existingAlbums.map(album => album.discogsId).filter(Boolean));
          let newAlbums = 0;
          let skippedAlbums = 0;
          
          collectionItems = allReleases.filter(item => {
            const isNew = !existingDiscogsIds.has(item.basic_information.id.toString());
            if (isNew) {
              newAlbums++;
            } else {
              skippedAlbums++;
            }
            return isNew;
          });
          
          setCollectionProgress({ 
            message: `Found ${collectionItems.length} new albums to process`, 
            percentage: 100,
            totalItems: allReleases.length,
            isIncremental: true,
            newAlbums,
            skippedAlbums
          });
        } else {
          collectionItems = allReleases;
          setCollectionProgress({ 
            message: `Found ${collectionItems.length} albums to process`, 
            percentage: 100,
            totalItems: allReleases.length,
            isIncremental: false
          });
        }
      } catch (error) {
        console.error('Failed to fetch Discogs collection:', error);
        alert('Failed to connect to Discogs. Please check your credentials and try again.');
        setSyncing(false);
        return;
      }

      if (collectionItems.length === 0) {
        const message = syncMode === 'incremental' 
          ? 'No new albums found in your Discogs collection.' 
          : 'No albums found in your Discogs collection.';
        alert(message);
        setSyncing(false);
        return;
      }

      // Step 2: Process each album
      const syncedAlbums: Album[] = [];
      const stats: SyncStats = {
        totalAlbums: collectionItems.length,
        processed: 0,
        succeeded: 0,
        failed: 0,
        errors: []
      };

      setProcessingProgress(stats);

      for (let i = 0; i < collectionItems.length; i++) {
        const item = collectionItems[i];
        const basicInfo = item.basic_information;
        const instanceId = item.instance_id.toString();

        // Skip if already processed (for resume functionality)
        if (processedInstanceIds.includes(instanceId)) {
          stats.processed = i + 1;
          // For resumed sync, check if album exists and count it as successful
          const existingAlbum = await secureSupabaseService.getAlbum(`collection-${item.instance_id}`);
          if (existingAlbum) {
            stats.succeeded++;
            syncedAlbums.push(existingAlbum);
          }
          setProcessingProgress({ ...stats });
          continue;
        }

        try {
          stats.processed = i + 1;
          setProcessingProgress({ ...stats });

          const albumId = `collection-${item.instance_id}`;
          const existingAlbum = await secureSupabaseService.getAlbum(albumId);
          
          // Check if this album needs syncing (unless force sync is enabled)
          if (existingAlbum && !forceSync && !albumUpdateService.needsSync(existingAlbum, 'discogs')) {
            console.log(`Skipping ${basicInfo.title} - recently synced`);
            syncedAlbums.push(existingAlbum);
            stats.succeeded++;
            continue;
          }

          // Get detailed release information with master release data for original year
          const release = await secureDiscogsService.getEnhancedRelease(basicInfo.id);

          // Convert to album format
          const albumData = convertDiscogsToAlbum(release, albumId);
          
          let finalAlbum: Album;
          
          if (existingAlbum) {
            // Merge with existing album, preserving custom data
            const { album: mergedAlbum, changes } = albumUpdateService.mergeAlbumData(
              existingAlbum,
              albumData,
              {
                preserveCustomFields: true,
                updateImages: false, // Only if missing
                updateTracklist: true,
                syncSource: 'discogs'
              }
            );
            
            finalAlbum = mergedAlbum;
            
            if (!changes.hasChanges && !changes.imageNeedsUpdate) {
              console.log(`No changes needed for ${basicInfo.title}`);
              syncedAlbums.push(existingAlbum);
              stats.succeeded++;
              continue;
            }
            
            console.log(`Updating ${basicInfo.title} - Changes:`, changes.changedFields);
            if (changes.preservedCustomFields.length > 0) {
              console.log(`Preserved custom fields:`, changes.preservedCustomFields);
            }
          } else {
            // New album - create from scratch
            finalAlbum = {
              id: albumId,
              artist: albumData.artist!,
              artists: albumData.artists || [albumData.artist!],
              title: albumData.title!,
              year: albumData.year!,
              releaseYear: albumData.releaseYear,
              coverImageBlob: new Blob(),
              coverImageUrl: undefined,
              genres: albumData.genres!,
              isVariousArtists: albumData.isVariousArtists!,
              discogsId: albumData.discogsId!,
              masterId: albumData.masterId,
              shelfPosition: { section: 0, estimatedPosition: 0, totalInSection: 0 },
              sortKey: '',
              format: albumData.format,
              tracklist: albumData.tracklist,
              syncMetadata: albumData.syncMetadata,
            };
          }

          // Handle cover image only if needed
          if (!existingAlbum || !finalAlbum.coverImageBlob || finalAlbum.coverImageBlob.size === 0) {
            if (release.images && release.images.length > 0) {
              const primaryImage = release.images.find(img => img.type === 'primary') || release.images[0];
              finalAlbum.coverImageUrl = primaryImage.uri;
              
              try {
                const coverImageBlob = await secureDiscogsService.downloadCoverImage(primaryImage.uri);
                if (coverImageBlob.size > 0) {
                  finalAlbum.coverImageBlob = coverImageBlob;
                }
              } catch (imgError) {
                console.log(`Image download failed for ${finalAlbum.title}, using URL fallback:`, imgError);
              }
            }
          }

          // Generate sort key
          finalAlbum.sortKey = generateSortKey(finalAlbum);

          // Save album
          await secureSupabaseService.saveAlbum(finalAlbum);
          console.log(`Saved album: ${finalAlbum.artist} - ${finalAlbum.title}`);
          
          // Save image if we have one
          if (finalAlbum.coverImageBlob && finalAlbum.coverImageBlob.size > 0) {
            try {
              await secureSupabaseService.saveImage(finalAlbum.id, finalAlbum.coverImageBlob);
            } catch (imageError) {
              console.error(`Failed to save image for ${finalAlbum.title}:`, imageError);
              stats.errors.push(`Image save failed for ${finalAlbum.title}: ${imageError}`);
            }
          }

          syncedAlbums.push(finalAlbum);
          stats.succeeded++;
          
          // Mark as processed and save progress
          processedInstanceIds.push(instanceId);
          currentProgress = {
            ...currentProgress,
            processedItems: stats.processed,
            successfulItems: stats.succeeded,
            failedItems: stats.failed,
            processedInstanceIds,
            errors: stats.errors,
            lastUpdateTime: new Date().toISOString()
          };
          await secureSupabaseService.updateSettings({ sync_progress: currentProgress });

        } catch (error: any) {
          const errorMsg = `Failed to sync ${basicInfo.title}: ${error.message}`;
          stats.errors.push(errorMsg);
          stats.failed++;
          console.error(errorMsg, error);
          
          // Mark as processed (even if failed) and save progress
          processedInstanceIds.push(instanceId);
          currentProgress = {
            ...currentProgress,
            processedItems: stats.processed,
            successfulItems: stats.succeeded,
            failedItems: stats.failed,
            processedInstanceIds,
            errors: stats.errors,
            lastUpdateTime: new Date().toISOString()
          };
          await secureSupabaseService.updateSettings({ sync_progress: currentProgress });
        }

        setProcessingProgress({ ...stats });
      }

      // Mark sync as complete
      currentProgress = {
        ...currentProgress,
        isComplete: true,
        processedItems: stats.totalAlbums,
        successfulItems: stats.succeeded,
        failedItems: stats.failed,
        lastUpdateTime: new Date().toISOString()
      };
      await secureSupabaseService.updateSettings({ sync_progress: currentProgress });

      // Load all albums from storage and notify parent component
      const allAlbumsFromStorage = await secureSupabaseService.getAllAlbums();
      console.log(`Sync completed. Total albums in storage: ${allAlbumsFromStorage.length}`);
      
      // Load images for albums that might not have them loaded
      for (const album of allAlbumsFromStorage) {
        if (!album.coverImageBlob) {
          const imageBlob = await secureSupabaseService.getImage(album.id);
          if (imageBlob) {
            album.coverImageBlob = imageBlob;
          }
        }
      }
      
      if (allAlbumsFromStorage.length > 0) {
        onAlbumsAdded(allAlbumsFromStorage);
      }

      // Auto-close after delay if mostly successful
      if (stats.failed < stats.totalAlbums * 0.1) { // Less than 10% failures
        setTimeout(async () => {
          await secureSupabaseService.updateSettings({ sync_progress: undefined });
          setSyncing(false);
          onClose();
        }, 3000);
      }

    } catch (error: any) {
      console.error('Collection sync failed:', error);
      alert(`Collection sync failed: ${error.message}`);
      setSyncing(false);
    }
  };

  const renderCollectionInfo = () => {
    if (!collectionInfo) {
      return (
        <div style={{ textAlign: 'center', padding: theme.spacing.lg }}>
          <p style={{ color: theme.colors.textSecondary }}>Loading collection info...</p>
        </div>
      );
    }

    return (
      <div style={{
        padding: theme.spacing.lg,
        backgroundColor: theme.colors.background,
        borderRadius: theme.borderRadius.md,
        marginBottom: theme.spacing.lg,
      }}>
        <h3 style={{ margin: `0 0 ${theme.spacing.md} 0`, color: theme.colors.text }}>
          Your Discogs Collection
        </h3>
        <div style={{ fontSize: '0.875rem', color: theme.colors.textSecondary, lineHeight: 1.6 }}>
          <div><strong>{collectionInfo.totalItems}</strong> albums in your Discogs collection</div>
          <div><strong>{existingAlbums.length}</strong> albums already in local collection</div>
          <div><strong>{collectionInfo.totalPages}</strong> pages to fetch (100 albums per page)</div>
          <div style={{ marginTop: theme.spacing.sm, fontStyle: 'italic' }}>
            {syncMode === 'incremental' 
              ? 'Incremental sync will only process new albums not yet in your local collection.'
              : 'Full sync will re-process your entire collection, including existing albums.'
            }
          </div>
        </div>
      </div>
    );
  };

  const renderSyncProgress = () => {
    if (!syncing) return null;

    return (
      <div style={{
        padding: theme.spacing.lg,
        backgroundColor: theme.colors.background,
        borderRadius: theme.borderRadius.md,
        marginTop: theme.spacing.lg,
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: theme.spacing.md }}>
          <h3 style={{ margin: 0, color: theme.colors.text }}>
            Syncing Collection...
          </h3>
          <button
            style={{
              ...buttonStyle,
              backgroundColor: theme.colors.success,
              width: 'auto',
              padding: `${theme.spacing.sm} ${theme.spacing.md}`,
              fontSize: '0.875rem'
            }}
            onClick={handleSetupStorage}
            disabled={settingUpStorage}
          >
            {settingUpStorage ? 'Setting up...' : '💾 Ensure Storage Ready'}
          </button>
        </div>
        
        
        {collectionProgress && (
          <div style={{ marginBottom: theme.spacing.lg }}>
            <div style={{ fontSize: '0.875rem', color: theme.colors.textSecondary, marginBottom: theme.spacing.sm }}>
              Step 1: {collectionProgress.isIncremental ? 'Finding New Albums' : 'Fetching from Discogs'}
            </div>
            <div style={progressBarStyle}>
              <div style={{
                height: '100%',
                backgroundColor: theme.colors.primary,
                width: `${collectionProgress.percentage}%`,
                transition: 'width 0.3s ease',
              }} />
            </div>
            <div style={{ fontSize: '0.875rem', color: theme.colors.text }}>
              {collectionProgress.currentAlbum || collectionProgress.message}
            </div>
            {collectionProgress.isIncremental && (collectionProgress.newAlbums !== undefined || collectionProgress.skippedAlbums !== undefined) && (
              <div style={{ 
                display: 'flex', 
                justifyContent: 'space-between',
                fontSize: '0.75rem',
                color: theme.colors.textSecondary,
                marginTop: '4px'
              }}>
                <span>🆕 {collectionProgress.newAlbums || 0} new</span>
                <span>⏭️ {collectionProgress.skippedAlbums || 0} skipped</span>
              </div>
            )}
          </div>
        )}

        {processingProgress && (
          <div>
            <div style={{ fontSize: '0.875rem', color: theme.colors.textSecondary, marginBottom: theme.spacing.sm }}>
              Step 2: Processing Albums ({processingProgress.processed}/{processingProgress.totalAlbums})
            </div>
            <div style={progressBarStyle}>
              <div style={{
                height: '100%',
                backgroundColor: theme.colors.success,
                width: `${(processingProgress.processed / processingProgress.totalAlbums) * 100}%`,
                transition: 'width 0.3s ease',
              }} />
            </div>
            <div style={{ 
              display: 'flex', 
              justifyContent: 'space-between',
              fontSize: '0.75rem',
              color: theme.colors.textSecondary,
              marginTop: '4px'
            }}>
              <span>✅ {processingProgress.succeeded} successful</span>
              <span>❌ {processingProgress.failed} failed</span>
            </div>

            {processingProgress.errors.length > 0 && (
              <div style={{
                marginTop: theme.spacing.md,
                padding: theme.spacing.sm,
                backgroundColor: `${theme.colors.error}20`,
                borderRadius: theme.borderRadius.sm,
                maxHeight: '150px',
                overflow: 'auto',
              }}>
                <strong style={{ color: theme.colors.error, fontSize: '0.75rem' }}>
                  Recent Errors ({processingProgress.errors.length}):
                </strong>
                {processingProgress.errors.slice(-5).map((error, index) => (
                  <div key={index} style={{ fontSize: '0.75rem', color: theme.colors.error, marginTop: '2px' }}>
                    {error}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    );
  };

  return (
    <div style={overlayStyle} onClick={!syncing ? onClose : undefined}>
      <div style={modalStyle} onClick={(e) => e.stopPropagation()}>
        <div style={headerStyle}>
          <h2 style={titleStyle}>Sync Discogs Collection</h2>
          {!syncing && (
            <button style={closeButtonStyle} onClick={onClose}>×</button>
          )}
        </div>

        {!syncing && (
          <>
            {pendingSyncProgress ? (
              <div style={{
                padding: theme.spacing.lg,
                backgroundColor: `${theme.colors.primary}20`,
                borderRadius: theme.borderRadius.md,
                marginBottom: theme.spacing.lg,
                border: `1px solid ${theme.colors.primary}40`
              }}>
                <h3 style={{ margin: `0 0 ${theme.spacing.sm} 0`, color: theme.colors.text }}>
                  Resume Previous Sync
                </h3>
                <div style={{ fontSize: '0.875rem', color: theme.colors.textSecondary, marginBottom: theme.spacing.md }}>
                  Found incomplete {pendingSyncProgress.mode} sync from{' '}
                  {new Date(pendingSyncProgress.startTime).toLocaleString()}
                </div>
                <div style={{ fontSize: '0.875rem', marginBottom: theme.spacing.md }}>
                  <div>Progress: {pendingSyncProgress.processedItems} / {pendingSyncProgress.totalItems} albums</div>
                  <div>✅ {pendingSyncProgress.successfulItems} successful</div>
                  <div>❌ {pendingSyncProgress.failedItems} failed</div>
                </div>
                <div style={{ display: 'flex', gap: theme.spacing.sm }}>
                  <button
                    style={{...buttonStyle, backgroundColor: theme.colors.success}}
                    onClick={handleResumeSync}
                  >
                    Resume Sync
                  </button>
                  <button
                    style={{...buttonStyle, backgroundColor: theme.colors.error}}
                    onClick={handleClearProgress}
                  >
                    Start Fresh
                  </button>
                </div>
              </div>
            ) : (
              <>
                <p style={{ 
                  color: theme.colors.textSecondary, 
                  marginBottom: theme.spacing.lg,
                  lineHeight: 1.5 
                }}>
                  This will automatically import your entire Discogs collection with enhanced metadata from Rate Your Music.
                </p>

                {renderCollectionInfo()}
              </>
            )}

            {collectionInfo && !pendingSyncProgress && (
              <>
                <div style={{
                  marginBottom: theme.spacing.lg,
                  padding: theme.spacing.md,
                  backgroundColor: theme.colors.surfaceLight,
                  borderRadius: theme.borderRadius.md,
                }}>
                  <h4 style={{ margin: `0 0 ${theme.spacing.sm} 0`, color: theme.colors.text, fontSize: '1rem' }}>
                    Sync Mode
                  </h4>
                  <div style={{ display: 'flex', gap: theme.spacing.md, marginBottom: theme.spacing.sm }}>
                    <label style={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      cursor: 'pointer',
                      color: theme.colors.text,
                      fontSize: '0.875rem'
                    }}>
                      <input
                        type="radio"
                        value="incremental"
                        checked={syncMode === 'incremental'}
                        onChange={() => setSyncMode('incremental')}
                        style={{ marginRight: theme.spacing.sm }}
                      />
                      Incremental (recommended)
                    </label>
                    <label style={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      cursor: 'pointer',
                      color: theme.colors.text,
                      fontSize: '0.875rem'
                    }}>
                      <input
                        type="radio"
                        value="full"
                        checked={syncMode === 'full'}
                        onChange={() => setSyncMode('full')}
                        style={{ marginRight: theme.spacing.sm }}
                      />
                      Full Collection
                    </label>
                  </div>
                  
                  {syncMode === 'full' && (
                    <div style={{ marginTop: theme.spacing.sm }}>
                      <label style={{ 
                        display: 'flex', 
                        alignItems: 'center', 
                        cursor: 'pointer',
                        color: theme.colors.text,
                        fontSize: '0.875rem'
                      }}>
                        <input
                          type="checkbox"
                          checked={forceSync}
                          onChange={(e) => setForceSync(e.target.checked)}
                          style={{ marginRight: theme.spacing.sm }}
                        />
                        Force re-sync (ignore 7-day cooldown)
                      </label>
                    </div>
                  )}
                  
                  <div style={{ fontSize: '0.75rem', color: theme.colors.textSecondary, marginTop: theme.spacing.sm }}>
                    {syncMode === 'incremental' && existingAlbums.length > 0
                      ? `Will check for new albums and skip ${existingAlbums.length} existing ones.`
                      : syncMode === 'incremental' && existingAlbums.length === 0
                      ? 'No existing albums found - will perform full sync.'
                      : syncMode === 'full'
                      ? forceSync 
                        ? `Will re-process all ${collectionInfo.totalItems} albums (ignoring 7-day cooldown).`
                        : `Will re-process all ${collectionInfo.totalItems} albums (only those not synced within 7 days).`
                      : `Will re-process all ${collectionInfo.totalItems} albums in your collection.`
                    }
                  </div>
                </div>


                <button
                  style={buttonStyle}
                  onClick={() => handleSyncCollection()}
                  disabled={!collectionInfo}
                >
                  {syncMode === 'incremental' && existingAlbums.length > 0
                    ? `Sync New Albums (${Math.max(0, collectionInfo.totalItems - existingAlbums.length)} estimated)`
                    : `Sync ${collectionInfo.totalItems} Albums from Collection`
                  }
                </button>
              </>
            )}
          </>
        )}

        {renderSyncProgress()}
      </div>
    </div>
  );
};

export default CollectionSyncModal;